/* eslint-disable */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { LanguageStorage, LngLocalization } from '../language/localization';
import { lng } from '../language/translations';
import { RootState } from './store';
import { Brand } from '../models/enum/Brand';

export interface FaqModel {
  title: string,
  pages: FaqPage[]
};

export interface FaqPage {
  title: string,
  categories: FaqCategory[]
};

export interface FaqCategory {
  title: string,
  qa: {
    q: string,
    a: string
  }[]
}

export interface AppState {
  appLanguage?: string,
  availableLanguages?: { code: string, description: string }[],
  bankName?: string,
  brand?: Brand,
  currentTranslation?: LngLocalization,
  hasReadPrivacyPolicy: boolean,
  isLoading: boolean,
  monthlyPriceDi: number,
  monthlyGrossPriceDi: number,
  monthlyGrossInstallmentPriceDi: number,
  monthlyPricePpi: number,
  monthlyGrossPricePpi: number,
  monthlyGrossInstallmentPricePpi: number,
  policiesAmended: number[],
  translations?: LanguageStorage,
  faq?: Record<string, FaqModel>,
}

const initialState: AppState = {
  appLanguage: localStorage.getItem("lang") || "de",
  availableLanguages: undefined,
  brand: Brand[(localStorage.getItem("brand") ?? "0") as keyof typeof Brand],
  hasReadPrivacyPolicy: (localStorage.getItem("hasReadPrivacyPolicy") as unknown) as boolean || false,
  isLoading: false,
  monthlyPriceDi: 0,
  monthlyGrossPriceDi: 0,
  monthlyGrossInstallmentPriceDi: 0,
  monthlyPricePpi: 0,
  monthlyGrossPricePpi: 0,
  monthlyGrossInstallmentPricePpi: 0,
  faq: undefined,
  policiesAmended: []
};

const appSlice = createSlice({
  name: "app",
  initialState: initialState,
  reducers: {
    changeLanguage(state, action: PayloadAction<string>) {
      if (state.appLanguage !== action.payload) {
        state.appLanguage = action.payload;
        localStorage.setItem("lang", action.payload);
      }
    },
    setBrand(state, action: PayloadAction<Brand>) {
      if (state.brand !== action.payload) {
        state.brand = action.payload;
        localStorage.setItem("brand", Number(action.payload).toString());
      }
    },
    readDataProtectionRegulations(state, action: PayloadAction<boolean>) {
      localStorage.setItem("hasReadPrivacyPolicy", action.payload.toString());
      state.hasReadPrivacyPolicy = action.payload;
    },
    setMonthlyPriceDi(state, action: PayloadAction<number>) {
      state.monthlyPriceDi = action.payload;
    },
    setMonthlyPricePpi(state, action: PayloadAction<number>) {
      state.monthlyPricePpi = action.payload;
    },
    setPPRates(state, action: PayloadAction<{monthlyNetPrice: number, monthlyGrossPrice: number, installmentGrossPrice: number }>) {
      state.monthlyPricePpi = action.payload.monthlyNetPrice;
      state.monthlyGrossPricePpi = action.payload.monthlyGrossPrice;
      state.monthlyGrossInstallmentPricePpi = action.payload.installmentGrossPrice;
    },
    setDIRates(state, action: PayloadAction<{monthlyNetPrice: number, monthlyGrossPrice: number, installmentGrossPrice: number }>) {
      state.monthlyPriceDi = action.payload.monthlyNetPrice;
      state.monthlyGrossPriceDi = action.payload.monthlyGrossPrice;
      state.monthlyGrossInstallmentPriceDi = action.payload.installmentGrossPrice;
    },
    setBankName(state, action: PayloadAction<string>) {
      state.bankName = action.payload;
    },
    setAllPoliciesAmended(state, action: PayloadAction<number[]>) {
      state.policiesAmended = action.payload;
    }
  },
  extraReducers(builder) {
    builder
      .addCase(loadLanguageConfig.fulfilled, (state, action) => {
        state.translations = action.payload;
        state.availableLanguages = [];
        Object.keys(action.payload).forEach(k => {
          if (!state.appLanguage && action.payload[k].isDefault)
            state.appLanguage = k;

          state.availableLanguages?.push({ code: k, description: action.payload[k].description })
        });
      })
      .addCase(loadFAQ.fulfilled, (state, action) => {
        state.faq = action.payload;
      })
  },
});

export const loadLanguageConfig = createAsyncThunk("loadLanguages", async (_, { getState }) => {
  try {
    const state = getState() as RootState;
    if (state.app.translations)
      return state.app.translations;

    return lng;
  }
  catch (err) {
    console.log(err);
  }

  return {};
})

export const loadFAQ = createAsyncThunk("loadFAQ", async (_, { getState }): Promise<Record<string, FaqModel>> => {
  try {
    const state = getState() as RootState;
    if (state.app.faq) return state.app.faq;

    const response = await fetch('assets/faq.json');
    return await response.json() as Record<string, FaqModel>;
  } catch (error) {
    throw error;
  }
});

export const { changeLanguage, setBrand, readDataProtectionRegulations, setMonthlyPriceDi, setMonthlyPricePpi, setDIRates, setPPRates, setBankName, setAllPoliciesAmended } = appSlice.actions;

// any -> RootState
export const selectedAppLanguage = (state: any) => state.app.appLanguage;
export const lngLocalization = (state: any) => {
  if (!state.app.translations || !state.app.appLanguage)
    return undefined;

  return state.app.translations[state.app.appLanguage];
}

export default appSlice.reducer;